import { Box, Button, Flex, Image, Img, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CenteredHeader } from 'src/components/common/texts/CenteredHeader/centeredHeader';
import { FORM_CONFIG } from '../../formConfig';
import { ArrowButton, Side } from './ArrowButton/arrowButton';
import { CartridgeAttribute, CartridgeAttributes } from './CartridgeAttributes/cartridgeAttributes';
import { cartridges } from './cartridges';
import { CartridgeCarousel } from './CartridgeCarousel/cartridgeCarousel';

import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
} from '@chakra-ui/react';
import { Color } from 'src/theme/color';
import { IconAssets } from 'src/config';
import { LoadSteamData } from '../../LoadSteamData/loadSteamData';

export enum CartridgeType {
	Gold = 'gold',
	Custom = 'custom',
	Gray = 'gray',
}

export interface CartridgeInfoType {
	type: CartridgeType;
	name: string;
	color: string;
	image: string;
	description: string;
	for: string;
	attributes: CartridgeAttribute[];
	maxSupply: number;
	number: number;
	maxFee: number;
}

export const SetCartridgeType = ({
	setSelectedCartridge,
	deletePrevious,
	nextStep,
	loadPrevious,
	backupAvailable,
}: {
	setSelectedCartridge: (color: CartridgeInfoType) => void;
	nextStep: () => void;
	loadPrevious: () => void;
	deletePrevious: () => void;
	backupAvailable: boolean;
}) => {
	const { values, setFieldValue } = useFormikContext();
	const [currentCartridge, setCurrentCartridge] = useState<number>(FORM_CONFIG.DEFAULT_CARTRIDGE_NUMBER);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { t } = useTranslation();

	useEffect(() => {
		setSelectedCartridge(cartridges[currentCartridge]);
		setFieldValue('cartridge', cartridges[currentCartridge].type);
	}, [currentCartridge, setSelectedCartridge]);

	const handleSwayForward = () => {
		setCurrentCartridge(previous => {
			const nextState = previous + 1;

			return nextState > 2 ? 0 : nextState;
		});
	};
	const handleSwayBackward = () => {
		setCurrentCartridge(previous => {
			const nextState = previous - 1;

			return nextState < 0 ? 2 : nextState;
		});
	};

	return (
		<Flex
			pos="relative"
			p="0px 110px"
			alignItems="center"
			margin="0 auto"
			flexDir="column"
			gap="40px"
			zIndex="0"
		>
			<Flex
				flexDirection="column"
				align="center"
				justify="center"
				pos="relative"
				position="relative"
			>
				<ArrowButton
					side={Side.LEFT}
					action={() => handleSwayBackward()}
				/>
				<CenteredHeader label={t('form.steps.setCartridge.label')}>
					{t('form.steps.setCartridge.heading')}
				</CenteredHeader>
				<CartridgeCarousel currentCartridge={currentCartridge} />
				<ArrowButton
					side={Side.RIGHT}
					action={() => handleSwayForward()}
				/>
			</Flex>
			<Box>
				<CartridgeAttributes
					color={t(cartridges[currentCartridge].color)}
					attributes={cartridges[currentCartridge].attributes}
				/>
			</Box>
			<Flex
				gap="25px"
				align="center"
			>
				<Button
					onClick={backupAvailable ? onOpen : nextStep}
					w="470px"
					disabled={currentCartridge == 0}
				>
					{t('form.steps.setCartridge.start')}
				</Button>
				<LoadSteamData />
			</Flex>
			{backupAvailable && <Box onClick={loadPrevious}>Load progress</Box>}
			<Modal
				isOpen={isOpen}
				onClose={onClose}
			>
				<ModalOverlay />
				<ModalContent
					margin="auto"
					border="1px solid"
					borderColor={Color.HASHUP}
					bg={Color.FORM_BACKGROUND}
					maxW="620px"
					padding="40px"
					display="flex"
					justifyContent="center"
					gap="30px"
					alignItems="center"
					textAlign="center"
				>
					<ModalHeader
						lineHeight="100%"
						m="0"
						p="0"
						maxW="350px"
					>
						{t('form.steps.setCartridge.modalHeading')}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody
						m="0"
						p="0"
						color={Color.GRAY_2}
						display="flex"
						justifyContent="center"
					>
						{t('form.steps.setCartridge.modalBody')}
					</ModalBody>

					<Flex
						justify="center"
						gap="8px"
					>
						<Button
							variant="ghost"
							onClick={() => {
								onClose();
								window.scrollTo(0, 0);
								loadPrevious();
							}}
						>
							{t('form.steps.setCartridge.load')}
						</Button>
						<Button
							onClick={() => {
								deletePrevious();
								onClose();
								nextStep();
							}}
						>
							{t('form.steps.setCartridge.new')}
						</Button>
					</Flex>
				</ModalContent>
			</Modal>
		</Flex>
	);
};
